import { useState } from "react";

import { useSearchState } from "common/router/hooks";

import Form from "client/common/components/forms/Form";
import Button from "client/common/components/buttons/Button";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import TextButton from "client/common/components/buttons/TextButton";
import TextInput from "client/common/components/inputs/TextInput";
import { Paths, urls } from "client/routes";

import { ResetPasswordConfirm } from "../../types";
import { useRequestResetPasswordLink } from "../../hooks";
import { toPhone } from "../../../../common/utils/formatting";
import AuthPage from "./AuthPage";

import styles from "../../styles/ResetPasswordPage.module.less";

type ResetPasswordFormProps = {
  onSuccess: (confirmation: ResetPasswordConfirm) => void;
};

const ResetPasswordForm = ({ onSuccess }: ResetPasswordFormProps) => {
  const [username, setUsername] = useState("");
  const { mutate: requestReset, isLoading, error } = useRequestResetPasswordLink({ onSuccess });

  const usernameErrors = error?.errors?.username ?? [];

  const canSubmit = !!username.length && !isLoading;

  return (
    <Form className={styles.form} onSubmit={() => requestReset({ username })} canSubmit={canSubmit}>
      {error && <FetchErrorAlert error={error} />}
      <TextInput
        label="Phone number or email"
        value={username}
        onChange={setUsername}
        errors={usernameErrors}
      />
      <Button type="submit" isLoading={isLoading} disabled={!canSubmit}>
        Submit
      </Button>
    </Form>
  );
};

const ResetPasswordPage = () => {
  const [search] = useSearchState<Paths["accounts"]["resetPassword"]>({});
  const [confirmation, setConfirmation] = useState<ResetPasswordConfirm>();

  if (confirmation) {
    const sendType = confirmation.phone_number ? "A text" : "An email";
    const toVal = confirmation.phone_number
      ? toPhone(confirmation.phone_number)
      : confirmation.email;

    const confirmMessage = (
      <>
        {sendType} has been sent to you at <strong>{toVal} </strong>
        with instructions for resetting your password.
        {confirmation.phone_number &&
          confirmation.user_email_exists &&
          " If you don't receive a text within a few minutes, please check your email for the instructions."}
      </>
    );

    return <AuthPage title="Request received" description={confirmMessage} />;
  }

  return (
    <AuthPage
      title="Reset password"
      description="Enter your phone number or email below to request a password reset link"
    >
      <div className={styles.container}>
        <ResetPasswordForm onSuccess={setConfirmation} />
        <div className={styles.help}>
          <TextButton dest={urls.accounts.signup({ search })}>Sign Up</TextButton>
          <span className={styles.space}>·</span>
          <TextButton dest={urls.accounts.login({ search })}>Login</TextButton>
        </div>
      </div>
    </AuthPage>
  );
};

export default ResetPasswordPage;
